'use client';

import { Skeleton } from '@workspace/ui/components/skeleton';
import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';
import { RefObject } from 'react';
import { ProjectCard } from './ProjectCard';

export interface ProjectSummary {
  id: number;
  title: string;
  shortDescription: string;
  systems: string[];
  createdAt: Date;
  author: {
    id: number;
    stripeAccountId: string | null;
    avatarUrl: string | null;
    authorFullName: string;
    uniqueLink: string;
    isPartner: boolean;
  };
  price: number | null;
  coverImageUrl: string;
  slug: string;
  creatorId: number;
  totalProjectsCount: number;
  rating: {
    average: number;
    total: number;
  };
}

type Props = {
  projects: ProjectSummary[];
  loading: boolean;
  hasNextPage: boolean;
  loadMore: () => void;
  loadMoreRef: RefObject<HTMLDivElement | null>;
  isProfile?: boolean;
  isSmall?: boolean;
  setIsChosen?: (value: number | null) => void;
  isChosen?: number | null;
};

export const ProfileProjectsList = ({
  projects,
  loading,
  hasNextPage,
  loadMore,
  loadMoreRef,
  isProfile,
  isSmall,
  setIsChosen,
  isChosen,
}: Props) => {
  if (loading) {
    return (
      <div className="flex flex-wrap gap-5 w-full mb-8">
        {Array.from({ length: 9 }).map((_, index) => (
          <Skeleton key={index} className="md:w-52 md:h-[340px] bg-bg-gray" />
        ))}
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="text-center py-12">
        {isProfile ? (
          <p className="text-gray-600">
            This user has not added any projects yet.
          </p>
        ) : (
          <p className="text-gray-600">No content found</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className={cn('flex flex-wrap gap-2 md:px-0 px-4 xl:min-w-[1368px]', {
          'xl:min-w-[904px] gap-6': isProfile,
          'xl:min-w-[500px] gap-3': isSmall,
        })}
      >
        {projects.map((project) => (
          <ProjectCard
            key={project.id}
            id={project.id}
            title={project.title}
            shortDescription={project.shortDescription}
            price={project.price}
            slug={project.slug}
            systems={project.systems}
            rating={project.rating}
            coverImageUrl={project.coverImageUrl}
            isSmall={isSmall}
            setIsChosen={setIsChosen}
            isChosen={isChosen}
          />
        ))}
      </div>

      {hasNextPage && (
        <div ref={loadMoreRef} className="flex justify-center py-8">
          <Button
            size="sm"
            onClick={loadMore}
            className="px-6 py-3 rounded-lg transition-colors"
          >
            Load More
          </Button>
        </div>
      )}

      {!hasNextPage && projects.length > 0 && !isProfile && (
        <div className="text-center py-8 text-gray-500">
          You've reached the end of the content
        </div>
      )}
    </>
  );
};
